/*
 * Chat between users, per extension (mt-prov-sf docs/chat.md), in the core
 * API (mt-vox docs/api.md, "Chat"). Same behaviour as the native core
 * (core/src/chat.c):
 *
 *   chat.list                                  {me, synced, enabled, unread, conversations}
 *   chat.messages {conversation, before?, limit?}   {messages, more}
 *   chat.send     {to | conversation, body, files?}   {id, conversation}
 *                 files: [{blob, name, type?}] (Blob or File), at most 10 of 10 MB;
 *                 the body may then be empty. Uploaded before the message goes.
 *   chat.read     {conversation, seq?}         (default: the last)
 *   chat.edit     {message, body}              (message: its id)
 *   chat.delete   {message}
 *   chat.sync                                  sync now
 * Events:
 *   chat.changed  {unread}                     list or messages anew
 *   chat.message  {conversation, message}      a new message from someone else
 *   chat.failed   {id, op, code, message}      the server refused a send / edit
 *
 * A conversation: {id, kind, title, members [{address, read_seq}], peer
 * (direct: the other address), last_seq, last_at, read_seq, unread, last}.
 * Until the server names it, a first conversation with someone is
 * "new:<address>" ("new:<address>|<from>" when written as another of the
 * device's extensions than line 1's: several lines, one inbox; each
 * conversation says as, which of mine it is). A message: {conversation, seq, id, from, body,
 * attachments, created_at, edited_at, deleted, pending, mine}. An
 * attachment: {id, name, type, size, url} (url: a signed link good for an
 * hour or more); a pending message's carry their blob instead.
 *
 * Sends, reads, edits and deletions are local first and queued (KV "chat"),
 * posted when the config carries chat_seq (chat on for the device); each
 * send carries an id made here, so a retry never doubles a message. Changes
 * come with GET /v1/chat?since=<cursor>. A browser gets no push: besides the
 * config's chat_seq, it asks every POLL_MS while chat is on.
 */
import type { Core, CoreModule } from "../core/core";
import { CommandError, Command, Json } from "../core/types";
import { ProvError, type ChatPartner, type Credential, type ProvClient } from "./client";
import { newId } from "./contacts";
import type { KV } from "./store";

const KEY = "chat";
const VERSION = 1;
const MAX_CACHED = 200;
const MAX_BODY = 4000;
const MAX_FILES = 10;
const MAX_FILE = 10 * 1024 * 1024;
const MAX_TRIES = 3; // a file's upload link refused: a new one
const DEBOUNCE_MS = 100;
const RETRY_MIN_MS = 5_000;
const RETRY_MAX_MS = 300_000;
const POLL_MS = 15_000;
const bytes = (s: string): number => new TextEncoder().encode(s).length;
const ADDRESS = /^ext:[A-Za-z0-9._\-*#+]+\/[A-Za-z0-9._\-*#+]+$/;

export interface ChatMember {
  address: string;
  read_seq: number;
}

export interface ChatAttachment {
  id?: string;
  name: string;
  type: string;
  size: number;
  url?: string;
  blob?: Blob; // pending: the file itself
}

/* A file of a queued send: uploaded once it has an id and is done */
interface QueuedFile {
  blob: Blob;
  name: string;
  type: string;
  size: number;
  id?: string;
  url?: string;
  done?: boolean;
  tries?: number;
}

/** PUT a blob to a signed link; the HTTP status (0: no answer) */
export type PutFn = (url: string, blob: Blob, type: string) => Promise<number>;

const realPut: PutFn = async (url, blob, type) => {
  try {
    const r = await fetch(url, { method: "PUT", body: blob, headers: { "Content-Type": type }, credentials: "omit", cache: "no-store" });
    return r.status;
  } catch {
    return 0;
  }
};

export interface ChatMessage {
  conversation: string;
  seq: number; // 0: pending, not on the server yet
  id: string;
  from: string;
  body: string;
  attachments?: ChatAttachment[];
  created_at: number;
  edited_at?: number | null;
  deleted: boolean;
}

interface Conv {
  id: string;
  as?: string; // which of the device's extensions (default: me)
  kind: string;
  title?: string | null;
  members: ChatMember[];
  last_seq: number;
  last_at: number;
  read_seq: number;
  unread: number;
  messages: ChatMessage[]; // by seq, pending ones last
}

interface Op {
  op: "send" | "read" | "edit" | "delete";
  id?: string;
  body?: string;
  to?: string;
  conversation?: string;
  seq?: number;
  files?: QueuedFile[];
  from?: string; // another extension of the device than line 1's
}

interface State {
  version: number;
  me: string;
  mine?: string[]; // every extension of the device (line 1's first)
  cursor: number;
  synced: boolean; // a first fetch done: no notifications for what it brings
  conversations: Conv[];
  ops: Op[];
}

export interface ChatTimers {
  set(fn: () => void, ms: number): unknown;
  clear(t: unknown): void;
  now(): number;
}

const realTimers: ChatTimers = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (t) => clearTimeout(t as ReturnType<typeof setTimeout>),
  now: () => Date.now(),
};

const empty = (): State => ({ version: VERSION, me: "", cursor: 0, synced: false, conversations: [], ops: [] });

const bySeq = (a: ChatMessage, b: ChatMessage): number => {
  if (!a.seq || !b.seq) return (a.seq ? 0 : 1) - (b.seq ? 0 : 1);
  return a.seq - b.seq;
};

export class ChatModule implements CoreModule {
  private st: State = empty();
  private loaded?: Promise<void>;
  private client?: ProvClient;
  private cred?: Credential;
  private server = false;
  private files = false; // the config's chat_files: files can go
  private partners: ChatPartner[] = []; // the config's chat_partners
  private busy = false;
  private again = false;
  private backoff = 0;
  private timer?: unknown;

  constructor(
    private core: Core,
    private kv: KV,
    private timers: ChatTimers = realTimers,
    private pollMs = POLL_MS,
    private putFile: PutFn = realPut,
  ) {}

  command(c: Command): Promise<Json | void> | undefined {
    if (!c.cmd.startsWith("chat.")) return undefined;
    return this.ready().then(async (): Promise<Json | void> => {
      switch (c.cmd) {
        case "chat.list":
          return this.list();
        case "chat.messages":
          return this.messages(c);
        case "chat.send":
          return this.send(c);
        case "chat.read":
          return this.read(c);
        case "chat.edit":
          return this.editOrDelete(c, false);
        case "chat.delete":
          return this.editOrDelete(c, true);
        case "chat.sync":
          if (!this.server) throw new CommandError("not_active", "chat is off for this device");
          this.kick(0);
          return;
        default:
          throw new CommandError("unknown_command", c.cmd);
      }
    });
  }

  stop(): void {
    if (this.timer !== undefined) this.timers.clear(this.timer);
    this.timer = undefined;
  }

  /** A config arrived (ProvModule): chat on, and sync when its seq moved. */
  async onConfig(client: ProvClient, cred: Credential, seq: number | undefined, files = false, partners: ChatPartner[] = []): Promise<void> {
    await this.ready();
    this.client = client;
    this.cred = cred;
    const was = this.server;
    this.server = seq !== undefined;
    if (seq === undefined) partners = [];
    if (files !== this.files || JSON.stringify(partners) !== JSON.stringify(this.partners)) {
      this.files = files;
      this.partners = partners;
      this.emitChanged();
    }
    if (!this.server) {
      this.stop();
      if (was) this.emitChanged();
      return;
    }
    if (!was || seq !== this.st.cursor || !this.st.synced || this.st.ops.length) this.kick(0);
  }

  /** The device left provisioning: the chats belong to the extension on the server. */
  async forget(): Promise<void> {
    await this.ready();
    this.stop();
    this.server = this.busy = this.again = false;
    this.client = this.cred = undefined;
    this.partners = [];
    this.st = empty();
    await this.kv.del(KEY);
    this.emitChanged();
  }

  // --- data -------------------------------------------------------------
  private ready(): Promise<void> {
    this.loaded ??= this.kv.get<State>(KEY).then((s) => {
      if (s && s.version === VERSION) this.st = { ...empty(), ...s };
    });
    return this.loaded;
  }

  private persist(): Promise<void> {
    return this.kv.set(KEY, this.st);
  }

  private conv(id: string): Conv | undefined {
    return this.st.conversations.find((c) => c.id === id);
  }

  /** one of the device's own addresses */
  private isMine(a: string | undefined): boolean {
    return !!a && (a === this.st.me || (this.st.mine ?? []).includes(a));
  }

  /** which of the device's extensions a conversation is */
  private as(c: Conv): string {
    return c.as || this.st.me;
  }

  /** the other member; in notes to self (a single member) oneself */
  private peer(c: Conv): string | undefined {
    const as = this.as(c);
    return c.members.find((m) => m.address !== as)?.address ?? c.members[0]?.address;
  }

  /** a conversation's id until the server names it */
  private pendingId(to: string, as: string | undefined): string {
    return !as || as === this.st.me ? `new:${to}` : `new:${to}|${as}`;
  }

  private find(id: string): { c: Conv; m: ChatMessage } | undefined {
    for (const c of this.st.conversations) {
      const m = c.messages.find((x) => x.id === id);
      if (m) return { c, m };
    }
    return undefined;
  }

  /* Puts a message (from the server or pending) in its conversation; true
   * when it wasn't there before */
  private put(c: Conv, m: ChatMessage): boolean {
    const was = this.find(m.id);
    if (was) was.c.messages = was.c.messages.filter((x) => x.id !== m.id);
    c.messages.push({ ...m });
    c.messages.sort(bySeq);
    // the newest MAX_CACHED with a seq (pending ones always stay)
    while (c.messages.length > MAX_CACHED && c.messages[0].seq) c.messages.shift();
    return !was;
  }

  private unreadOf(c: Conv): number {
    return c.messages.filter((m) => m.seq > c.read_seq && !m.deleted && !this.isMine(m.from)).length;
  }

  private total(): number {
    return this.st.conversations.reduce((n, c) => n + c.unread, 0);
  }

  private view(m: ChatMessage): Json {
    return { ...m, pending: m.seq === 0, mine: this.isMine(m.from) } as unknown as Json;
  }

  private emitChanged(): void {
    this.core.event("chat.changed", { unread: this.total() });
  }

  private async changed(): Promise<void> {
    for (const c of this.st.conversations) c.unread = this.unreadOf(c);
    await this.persist();
    this.emitChanged();
  }

  // --- commands ---------------------------------------------------------
  private list(): Json {
    const conversations = this.st.conversations.map((c) => {
      const { messages, ...meta } = c;
      const last = messages[messages.length - 1];
      const e: Record<string, unknown> = { ...meta, peer: this.peer(c), as: this.as(c) };
      if (last) {
        e.last = this.view(last);
        // a pending message sorts the conversation too
        if (!last.seq) e.last_at = last.created_at;
      }
      return e;
    });
    return {
      me: this.st.me,
      mine: this.st.mine?.length ? this.st.mine : this.st.me ? [this.st.me] : [],
      synced: this.st.synced,
      enabled: this.server,
      files: this.files,
      partners: this.partners,
      unread: this.total(),
      conversations,
    } as unknown as Json;
  }

  private messages(c: Command): Json {
    const conv = this.conv(String(c.conversation ?? ""));
    if (!conv) throw new CommandError("not_found", "conversation");
    const before = Number(c.before ?? 0) || 0;
    let limit = Number(c.limit ?? 50) || 50;
    if (limit > MAX_CACHED) limit = 50;
    const all = conv.messages.filter((m) => !before || (m.seq && m.seq < before));
    const out = all.slice(Math.max(0, all.length - limit));
    const oldest = out.reduce((o, m) => (m.seq && (!o || m.seq < o) ? m.seq : o), 0);
    // older than what is kept here: the server has them
    if (oldest > 1 && all.length < limit && !conv.id.startsWith("new:")) void this.history(conv.id, oldest, limit - all.length);
    return { messages: out.map((m) => this.view(m)), more: oldest > 1 } as unknown as Json;
  }

  private async history(id: string, before: number, limit: number): Promise<void> {
    if (!this.client || !this.cred) return;
    try {
      const r = await this.client.call<{ messages: ChatMessage[] }>(
        "GET",
        `/v1/chat/${encodeURIComponent(id)}/messages?before=${before}&limit=${limit}`,
        this.cred,
      );
      const c = this.conv(id);
      if (!c) return;
      for (const m of r.messages ?? []) this.put(c, m);
      await this.changed();
    } catch {
      /* next time */
    }
  }

  private async send(c: Command): Promise<Json> {
    const body = typeof c.body === "string" ? c.body : "";
    const files = this.takeFiles(c.files);
    // the server has no files service: say so now
    if (files.length && this.server && !this.files) throw new CommandError("attachments_off", "files");
    // a file alone is a message
    if ((!body.trim() && !files.length) || bytes(body) > MAX_BODY) throw new CommandError("invalid", "body");
    // from: one of the device's extensions
    const from = c.from === undefined ? undefined : String(c.from);
    if (from !== undefined && !this.isMine(from)) throw new CommandError("invalid", "from");
    let conv: Conv | undefined;
    let as: string;
    if (c.conversation !== undefined) {
      conv = this.conv(String(c.conversation));
      if (!conv) throw new CommandError("not_found", "conversation");
      if (from !== undefined && from !== this.as(conv)) throw new CommandError("invalid", "from");
      as = this.as(conv);
    } else {
      const to = String(c.to ?? "");
      if (!ADDRESS.test(to) || to.length >= 128) throw new CommandError("invalid", "to");
      as = from ?? this.st.me;
      conv =
        this.st.conversations.find((x) => x.kind === "direct" && this.peer(x) === to && this.as(x) === as) ??
        this.conv(this.pendingId(to, as));
      if (!conv) {
        // until the server names it
        conv = {
          id: this.pendingId(to, as),
          as,
          kind: "direct",
          members: [
            { address: as, read_seq: 0 },
            { address: to, read_seq: 0 },
          ],
          last_seq: 0,
          last_at: 0,
          read_seq: 0,
          unread: 0,
          messages: [],
        };
        this.st.conversations.push(conv);
      }
    }
    const id = newId("m", 30);
    const op: Op = { op: "send", id, body };
    if (files.length) op.files = files;
    if (conv.id.startsWith("new:")) op.to = this.peer(conv);
    else op.conversation = conv.id;
    if (as !== this.st.me) op.from = as;
    this.st.ops.push(op);
    // shown at once, pending
    this.put(conv, {
      conversation: conv.id,
      seq: 0,
      id,
      from: as,
      body,
      created_at: Math.floor(this.timers.now() / 1000),
      deleted: false,
      ...(files.length ? { attachments: files.map((f) => ({ name: f.name, type: f.type, size: f.size, blob: f.blob })) } : {}),
    });
    await this.changed();
    this.kick(0);
    return { id, conversation: conv.id };
  }

  private takeFiles(list: unknown): QueuedFile[] {
    if (list === undefined) return [];
    if (!Array.isArray(list) || list.length > MAX_FILES) throw new CommandError("invalid", "files");
    return list.map((f) => {
      const blob = (f as { blob?: unknown })?.blob;
      if (!(blob instanceof Blob) || blob.size < 1 || blob.size > MAX_FILE) throw new CommandError("invalid", "files");
      const name = String((f as { name?: unknown }).name ?? (blob as File).name ?? "fichier").slice(0, 200) || "fichier";
      const type = String((f as { type?: unknown }).type ?? (blob.type || "application/octet-stream")) || "application/octet-stream";
      return { blob, name, type: type.toLowerCase(), size: blob.size };
    });
  }

  private async read(c: Command): Promise<void> {
    const conv = this.conv(String(c.conversation ?? ""));
    if (!conv || conv.id.startsWith("new:")) throw new CommandError("not_found", "conversation");
    const seq = c.seq !== undefined ? Number(c.seq) : conv.last_seq;
    if (!(seq > conv.read_seq)) return;
    conv.read_seq = seq;
    this.st.ops.push({ op: "read", conversation: conv.id, seq });
    await this.changed();
    this.kick(DEBOUNCE_MS);
  }

  private async editOrDelete(c: Command, del: boolean): Promise<void> {
    const id = String(c.message ?? "");
    const f = id ? this.find(id) : undefined;
    if (!f || !this.isMine(f.m.from)) throw new CommandError("not_found", "message");
    const body = typeof c.body === "string" ? c.body : "";
    if (!del && (!body.trim() || bytes(body) > MAX_BODY)) throw new CommandError("invalid", "body");
    this.st.ops.push(del ? { op: "delete", id } : { op: "edit", id, body });
    // shown at once
    f.m.body = del ? "" : body;
    if (del) f.m.deleted = true;
    await this.changed();
    this.kick(0);
  }

  // --- sync -------------------------------------------------------------
  private kick(ms: number): void {
    if (!this.server) return;
    if (this.busy) {
      this.again = true;
      return;
    }
    if (this.timer !== undefined) this.timers.clear(this.timer);
    this.timer = this.timers.set(() => {
      this.timer = undefined;
      void this.run();
    }, ms);
  }

  private async run(): Promise<void> {
    if (!this.server || this.busy || !this.client || !this.cred) return;
    this.busy = true;
    try {
      while (this.st.ops.length) await this.post(this.st.ops[0]);
      await this.fetch();
      await this.done(true);
    } catch (e) {
      if (e instanceof ProvError && e.status === 403) this.server = false; // chat turned off meanwhile
      await this.done(false);
    }
  }

  private async done(ok: boolean): Promise<void> {
    this.busy = false;
    await this.persist();
    if (!this.server) return;
    if (!ok) {
      this.backoff = this.backoff ? Math.min(this.backoff * 2, RETRY_MAX_MS) : RETRY_MIN_MS;
      this.timer = this.timers.set(() => {
        this.timer = undefined;
        void this.run();
      }, this.backoff);
      return;
    }
    this.backoff = 0;
    if (this.again || this.st.ops.length) {
      this.again = false;
      this.kick(0);
    } else {
      this.kick(this.pollMs); // no push for browsers
    }
  }

  /* The first queued op; a refusal (4xx but 401) drops it and says so,
   * anything else throws and is retried later */
  private async post(op: Op): Promise<void> {
    const client = this.client!;
    const cred = this.cred!;
    let stored: ChatMessage | undefined;
    try {
      switch (op.op) {
        case "send": {
          if (!(await this.upload(op))) return; // dropped: refused
          const attachments = op.files?.map((f) => f.id);
          stored = await client.call<ChatMessage>(
            "POST",
            "/v1/chat/messages",
            cred,
            op.conversation && !op.conversation.startsWith("new:")
              ? { id: op.id, conversation: op.conversation, body: op.body, ...(attachments ? { attachments } : {}), ...(op.from ? { from: op.from } : {}) }
              : { id: op.id, to: op.to, body: op.body, ...(attachments ? { attachments } : {}), ...(op.from ? { from: op.from } : {}) },
          );
          break;
        }
        case "read":
          await client.call("POST", `/v1/chat/${encodeURIComponent(op.conversation ?? "")}/read`, cred, { seq: op.seq });
          break;
        case "edit":
          stored = await client.call<ChatMessage>("PATCH", `/v1/chat/messages/${encodeURIComponent(op.id ?? "")}`, cred, {
            body: op.body,
          });
          break;
        case "delete":
          stored = await client.call<ChatMessage>("DELETE", `/v1/chat/messages/${encodeURIComponent(op.id ?? "")}`, cred);
          break;
      }
    } catch (e) {
      if (!(e instanceof ProvError) || e.status === 0 || e.status === 401 || e.status >= 500) throw e;
      this.core.event("chat.failed", { id: op.id ?? "", op: op.op, code: e.code, message: e.message });
      // a send refused: the pending message goes
      if (op.op === "send") {
        const f = this.find(op.id ?? "");
        if (f) f.c.messages = f.c.messages.filter((m) => m.id !== op.id);
      }
      this.st.ops.shift();
      await this.changed();
      return;
    }
    this.st.ops.shift();
    if (stored?.conversation) {
      const cid = stored.conversation;
      let c = this.conv(cid);
      if (!c) {
        // until the next fetch names the members
        const pend = op.to ? this.conv(this.pendingId(op.to, op.from)) : undefined;
        c = {
          id: cid,
          ...(op.from ? { as: op.from } : {}),
          kind: "direct",
          members: pend?.members ?? [],
          last_seq: 0,
          last_at: 0,
          read_seq: 0,
          unread: 0,
          messages: [],
        };
        this.st.conversations.push(c);
        if (pend) this.absorb(c, pend);
      }
      this.put(c, stored);
      // other ops of the queue on "new:<to>" now have a name
      for (const q of this.st.ops) if (op.to && q.to === op.to && q.from === op.from && !q.conversation) q.conversation = cid;
    }
    await this.changed();
  }

  /* A send's files: an upload link each, then the bytes (every step saved:
   * a reload resumes). False when the send was refused and dropped;
   * throws to retry later. */
  private async upload(op: Op): Promise<boolean> {
    for (const f of op.files ?? []) {
      while (!f.done) {
        if (!f.url) {
          try {
            const r = await this.client!.call<{ id: string; upload_url: string }>("POST", "/v1/chat/attachments", this.cred!, {
              name: f.name,
              type: f.type,
              size: f.size,
            });
            f.id = r.id;
            f.url = r.upload_url;
          } catch (e) {
            if (e instanceof ProvError && e.status >= 400 && e.status < 500 && e.status !== 401) {
              await this.refuse(op, e.code || "upload_refused", e.message);
              return false;
            }
            throw e;
          }
          await this.persist();
        }
        const st = await this.putFile(f.url, f.blob, f.type);
        if (st === 201 || st === 409) f.done = true; // there (409: already)
        else if (st === 413) {
          await this.refuse(op, "too_large", "file too large");
          return false;
        } else if (st === 403) {
          // the link expired: a new one
          f.tries = (f.tries ?? 0) + 1;
          if (f.tries >= MAX_TRIES) {
            await this.refuse(op, "upload_refused", "upload link refused");
            return false;
          }
          f.id = f.url = undefined;
        } else throw new ProvError(st, "unreachable", "upload");
        await this.persist();
      }
    }
    return true;
  }

  /* The send can't go: dropped, as a refused send is */
  private async refuse(op: Op, code: string, message: string): Promise<void> {
    this.core.event("chat.failed", { id: op.id ?? "", op: op.op, code, message });
    const f = this.find(op.id ?? "");
    if (f) f.c.messages = f.c.messages.filter((m) => m.id !== op.id);
    this.st.ops.shift();
    await this.changed();
  }

  /* The pending conversation's messages move to the named one */
  private absorb(c: Conv, pend: Conv): void {
    for (const m of pend.messages) this.put(c, { ...m, conversation: m.seq ? m.conversation : c.id });
    this.st.conversations = this.st.conversations.filter((x) => x !== pend);
  }

  private async fetch(): Promise<void> {
    const r = await this.client!.call<{ me?: string; mine?: string[]; cursor: number; conversations: (Omit<Conv, "messages"> & { messages?: ChatMessage[]; more?: boolean })[] }>(
      "GET",
      `/v1/chat?since=${this.st.cursor}`,
      this.cred!,
    );
    if (r.me) this.st.me = r.me;
    if (Array.isArray(r.mine)) this.st.mine = r.mine;
    const notify = this.st.synced;
    for (const e of r.conversations ?? []) {
      const { messages, more: _more, ...meta } = e;
      let c = this.conv(e.id);
      if (c) Object.assign(c, meta);
      else {
        c = { ...meta, members: meta.members ?? [], unread: 0, messages: [] } as Conv;
        this.st.conversations.push(c);
      }
      const p = this.peer(c);
      const pend = p ? this.conv(this.pendingId(p, this.as(c))) : undefined;
      if (pend) this.absorb(c, pend);
      for (const m of messages ?? []) {
        if (this.put(c, m) && notify && !m.deleted && !this.isMine(m.from)) {
          this.core.event("chat.message", { conversation: c.id, as: this.as(c), message: this.view(m) as never });
        }
      }
    }
    this.st.cursor = r.cursor;
    this.st.synced = true;
    await this.changed();
  }
}
